import React from 'react';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import BadgeIcon from '@mui/icons-material/Badge';
import CategoryIcon from '@mui/icons-material/Category';
import PlaceIcon from '@mui/icons-material/Place';
import NotesIcon from '@mui/icons-material/Notes';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SectionTitle from '../shared/SectionTitle';
import InfoSection, { InfoRow } from '../shared/InfoSection';
import { t } from '../../../../utils/textUtils';
import { formatStatus } from '../utils/formatters';
import { getStatusStyle } from '../utils/theme';

const GeneralInfoTab = ({ project }) => {
  const statusStyle = getStatusStyle(project.status);
  const hasCoords = project.latitude && project.longitude;

  return (
    <>
      <SectionTitle
        title={t('INFORMASI UMUM')}
        subtitle={t('Ringkasan identitas proyek dan lokasi utama.')}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
          gap: 2,
          mb: 2
        }}
      >
        {/* Identifikasi */}
        <InfoSection title={t('IDENTIFIKASI')} icon={<BadgeIcon />} accent="#6366F1">
          <InfoRow
            label={t('NAMA PROYEK (LEVEL 1)')}
            value={project.name}
          />
          <InfoRow
            label={t('KODE PROYEK')}
            value={project.kodeProyek || project.projectCode}
          />
          <InfoRow label={t('NO PO')} value={project.noPO} />
        </InfoSection>

        {/* Klasifikasi */}
        <InfoSection title={t('KLASIFIKASI')} icon={<CategoryIcon />} accent="#8B5CF6">
          <InfoRow label={t('JENIS PROYEK')} value={project.jenisProyek} />
          <InfoRow label={t('SEKTOR')} value={project.sektor} />
          <InfoRow label={t('STATUS')}>
            <Chip
              label={formatStatus(project.status)}
              size="small"
              sx={{
                bgcolor: statusStyle.bg,
                color: statusStyle.color,
                fontWeight: 700,
                fontSize: '0.7rem',
                height: 22,
                mt: 0.25,
                '& .MuiChip-label': { px: 1.2 }
              }}
              icon={
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: statusStyle.dot,
                    ml: '6px !important'
                  }}
                />
              }
            />
          </InfoRow>
        </InfoSection>

        {/* Lokasi */}
        <InfoSection
          title={t('LOKASI')}
          icon={<PlaceIcon />}
          accent="#EF4444"
          action={
            hasCoords && (
              <Button
                size="small"
                variant="text"
                endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                component="a"
                href={`https://www.google.com/maps?q=${project.latitude},${project.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  color: '#2563EB',
                  textTransform: 'none',
                  py: 0.25,
                  '&:hover': { bgcolor: 'rgba(37, 99, 235, 0.06)' }
                }}
              >
                {t('LIHAT DI MAPS')}
              </Button>
            )
          }
        >
          <InfoRow label={t('ALAMAT')} value={project.location} />
          <InfoRow label={t('KOORDINAT (PIN MAPS)')}>
            {hasCoords ? (
              <Typography
                variant="body2"
                component="a"
                href={`https://www.google.com/maps?q=${project.latitude},${project.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontWeight: 600,
                  color: '#2563EB',
                  textDecoration: 'none',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                {project.latitude}, {project.longitude}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: '#94A3B8', fontWeight: 500 }}>
                —
              </Typography>
            )}
          </InfoRow>
        </InfoSection>
      </Box>

      {/* Description - full width readable block */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'rgba(15, 23, 42, 0.1)',
          bgcolor: '#fff',
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)'
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ mb: 2, pb: 2, borderBottom: '1px solid', borderColor: 'rgba(15,23,42,0.08)' }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              bgcolor: '#3B82F618',
              color: '#3B82F6',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& svg': { fontSize: 20, color: '#3B82F6' }
            }}
          >
            <NotesIcon />
          </Box>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: '#0f172a',
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              fontSize: '0.82rem'
            }}
          >
            {t('DESKRIPSI')}
          </Typography>
        </Stack>

        {project.description ? (
          <Typography
            variant="body1"
            sx={{
              color: '#334155',
              lineHeight: 1.75,
              fontSize: '0.95rem',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere'
            }}
          >
            {project.description}
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{ color: '#94A3B8', fontStyle: 'italic', fontSize: '0.9rem' }}
          >
            {t('TIDAK ADA DESKRIPSI TERSEDIA')}
          </Typography>
        )}
      </Paper>
    </>
  );
};

export default GeneralInfoTab;
